'use client';

import HeroVideo from '@/components/HeroVideo';
import { PricingAdvantageTooltip } from '@/components/PricingAdvantageTooltip';
import { CheckCircle } from 'lucide-react';
import Link from 'next/link';

import { GeneratorIcon } from '@/components/sora/GeneratorIcon';

import '@/styles/hero.css';

import type { GeneratorMetadata } from '@/lib/types/generator';

interface GeneratorHeroSectionProps {
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  generator?: GeneratorMetadata;
  headingLevel?: 'h1' | 'h2' | 'h3';
}

export default function GeneratorHeroSection({
  id = 'home',
  className = 'hero',
  style,
  generator,
  headingLevel: HeadingTag = 'h1',
}: GeneratorHeroSectionProps) {
  const titleParts = generator
    ? generator.modelName.split(' - ')
    : ['Sora 2', 'AI Video Generator'];
  const title1 = titleParts[0];
  const title2 = titleParts.slice(1).join(' - ');

  const heroLine1 =
    generator?.heroLine1 || 'Generate Stunning, Cinema-Quality Videos from Text—Today.';
  const heroLine2 =
    generator?.heroLine2 ||
    'Skip the waitlist. No complex setup. Just type your vision and get professional, commercially licensed video in minutes.';

  const bullet1 = generator?.bulletPoints[0] || {
    text: 'Unrestricted Access',
    subtext: 'Direct pipeline to Sora 2.',
  };
  const bullet2 = generator?.bulletPoints[1] || {
    text: '55% More Videos',
    subtext: 'Get more at lowest cost.',
  };
  const bullet3 = generator?.bulletPoints[2] || {
    text: 'Commercial License',
    subtext: '100% owned. No watermarks.',
  };

  const ctaText = generator?.ctaText || 'Video';
  const heroVideoUrl = generator?.heroVideoUrl ?? '/videos/sora-lite.mp4';
  const generatorLink = generator
    ? `/${generator.slug}#generator`
    : '/sora-2-ai-video-generator#generator';
  return (
    <section id={id} className={className} style={style}>
      <div className="hero-background">
        <HeroVideo
          src={heroVideoUrl}
          poster={generator?.heroPosterUrl ?? '/videos/sora-optimized.webp'}
          className="hero-background-video sora-hero-video"
          filter="brightness(0.9) contrast(1.1)"
          lazyLoad={true}
          priority={true}
        />
        <div className="hero-background-overlay sora-hero-overlay" />
      </div>
      <div className="container">
        <div className="hero-content">
          <GeneratorIcon generator={generator} className="mb-6 md:mb-8" size={80} />
          <HeadingTag className="hero-title !text-5xl md:!text-6xl">
            {title1} <br />
            {title2 && (
              <>
                {title2} <br />
              </>
            )}
          </HeadingTag>

          <h2 className="!text-xl mt-4 mb-6 font-bold text-white leading-tight md:text-3xl">
            {heroLine1}
          </h2>

          <p className="hero-subtitle">{heroLine2}</p>

          <div className="mt-8 mb-10 grid grid-cols-3 gap-2 text-center md:my-12 md:gap-6">
            <div className="flex flex-col items-center gap-0.5 md:cursor-default md:gap-2">
              <div className="flex flex-col items-center gap-1 font-bold text-[10px] text-white md:flex-row md:gap-2 md:text-base">
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-400 md:h-5 md:w-5" />
                <span className="leading-tight">{bullet1.text}</span>
              </div>
              <p className="mt-1 block text-center text-[10px] text-gray-200 md:text-sm">
                {bullet1.subtext}
              </p>
            </div>
            <div className="flex flex-col items-center gap-0.5 md:cursor-default md:gap-2">
              <div className="flex flex-col items-center gap-1 font-bold text-[10px] text-white md:flex-row md:gap-2 md:text-base">
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-400 md:h-5 md:w-5" />
                <span className="leading-tight">{bullet2.text}</span>
              </div>
              <p className="mt-1 block text-[10px] text-gray-200 md:text-sm">
                {bullet2.subtext}{' '}
                {bullet2.text === '55% More Videos' && (
                  <PricingAdvantageTooltip className="text-white hover:text-gray-300" />
                )}
              </p>
            </div>
            <div className="flex flex-col items-center gap-0.5 md:cursor-default md:gap-2">
              <div className="flex flex-col items-center gap-1 font-bold text-[10px] text-white md:flex-row md:gap-2 md:text-base">
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-400 md:h-5 md:w-5" />
                <span className="leading-tight">{bullet3.text}</span>
              </div>
              <p className="mt-1 block text-[10px] text-gray-200 md:text-sm">{bullet3.subtext}</p>
            </div>
          </div>

          <div className="hero-buttons">
            <Link href={generatorLink} className="btn btn-primary">
              Generate&nbsp;<span className="btn-text-extra">{ctaText}</span>
            </Link>
            {generator?.explore && (
              <button
                className="btn btn-secondary explore-link"
                onClick={() => {
                  const el = document.getElementById('explore');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Explore
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
